import type { ItemStatusSliceType } from "../services/redux/slices/itemStatusSlice";
import { LocalizedString } from "../services/redux/types/CartLoyaltyV2Types";
import { I18LanguagesEnum } from "./enums";
type LoyaltyNameLanguage = Record<I18LanguagesEnum, string>;

export enum LoyaltyOrderType {
  PICK_UP = "PICK_UP",
  DINE_IN = "DINE_IN",
  DRIVE_THRU = "DRIVE_THRU",
  HOME_DELIVERY = "HOME_DELIVERY",
  OFFICE_DELIVERY = "OFFICE_DELIVERY",
  CABIN_DELIVERY = "CABIN_DELIVERY",
  BEACH_DELIVERY = "BEACH_DELIVERY",
}

export enum LoyaltyChannel {
  MOBILE_APP = "MOBILE_APP",
  WEB_APP = "WEB_APP",
  SELF_SERVICE = "SELF_SERVICE",
  CALL_CENTER = "CALL_CENTER",
  CASHIER = "CASHIER",
}

export enum LoyaltyMechanism {
  PAY_WITH_POINTS = "PAY_WITH_POINTS",
  PAY_WITH_POINTS_AND_TIERS = "PAY_WITH_POINTS_AND_TIERS",
  POINTS_TO_REWARDS = "POINTS_TO_REWARDS",
  POINTS_TO_REWARDS_AND_TIERS = "POINTS_TO_REWARDS_AND_TIERS",
}

/**
 * Mechanisms that actually move members between tiers. The other two run a
 * single flat tier, so the tier carousel, the tier comparison screen and the
 * tier tinting have nothing to show for them.
 */
export const TIERED_LOYALTY_MECHANISMS: readonly LoyaltyMechanism[] = [
  LoyaltyMechanism.PAY_WITH_POINTS_AND_TIERS,
  LoyaltyMechanism.POINTS_TO_REWARDS_AND_TIERS,
];

/**
 * Mechanisms that spend points on milestone rewards. The PAY_WITH_POINTS pair
 * spends points against the bill instead, so there is no meter to plot and no
 * reward cards to slide.
 */
export const MILESTONE_LOYALTY_MECHANISMS: readonly LoyaltyMechanism[] = [
  LoyaltyMechanism.POINTS_TO_REWARDS,
  LoyaltyMechanism.POINTS_TO_REWARDS_AND_TIERS,
];

export type CustomerLoyaltyOffersParams = {
  /**
   * The branch, when the guest has picked one. Offline mode never does - it has
   * no ordering context - so the proxy resolves the chain from `restaurantId`
   * instead and picks the branch itself.
   */
  franchiseId?: number;
  restaurantId: number;
  orderType: LoyaltyOrderType;
  channel: LoyaltyChannel;
  includesSplashOffers?: boolean;
  includesMilestoneOffers?: boolean;
  offerFormat?: "raw" | "categorized";
  offerUi?: "full" | "text";
  /** Comma-separated language codes for offer ui-details localization. */
  languages?: string;
  customerShortId?: string;
};

export type LoyaltyCustomerInfoParams = {
  restaurantId: number;
  includes?: "tierProgress";
  /**
   * Offline-mode guests have no customer JWT: the short id from the QR link
   * identifies the customer and the proxy resolves it server-side. Signed-in
   * customers send nothing and are identified by their token. Same contract as
   * the customer-offers call above.
   */
  customerShortId?: string;
};

type LoyaltyAvailableMilestone = {
  milestoneId: string;
  availableOffers: string[];
};

export type LoyaltyTierProgress = {
  currentTierId: string;
  currentTierName: LoyaltyNameLanguage;
  nextTierId: string | null;
  nextTierName: LoyaltyNameLanguage | null;
  tierSystemType: string;
  remainingUnitsToStay: number;
  remainingUnitsToUpgrade: number | null;
  /** Null when the tier has no cooldown period to count down. */
  remainingDaysToResetCooldownPeriod: number | null;
  availableMilestones: LoyaltyAvailableMilestone[];
};

type LoyaltyPartnership = {
  name: string;
  groupName: string;
  groupId: string;
  isVerified?: boolean;
};

/**
 * Soonest-expiring points bucket, from the guest customer-info response
 * (`getSoonestExpiringPoints`). Absent when nothing is due, so consumers render
 * no hint rather than a zero.
 */
type LoyaltyPointsExpiry = {
  /** Points expiring on `expiresAt`. */
  amount: number;
  /** ISO date the bucket expires on. */
  expiresAt: string;
};

export type LoyaltyCustomerInfo = {
  currentPoints: number;
  availablePoints: number;
  onHoldPoints: number;
  redemptionRate?: number;
  earningRate?: number;
  tierProgress?: LoyaltyTierProgress;
  partnership?: LoyaltyPartnership;
  pointsExpiry?: LoyaltyPointsExpiry;
};

export enum LoyaltyDayOfWeek {
  MON = "MON",
  TUE = "TUE",
  WED = "WED",
  THU = "THU",
  FRI = "FRI",
  SAT = "SAT",
  SUN = "SUN",
}

export enum LoyaltyTargetType {
  ITEMS = "ITEMS",
  CATEGORIES = "CATEGORIES",
}

export enum LoyaltyRewardType {
  DISCOUNT = "DISCOUNT",
  POINTS = "POINTS",
}

export enum LoyaltyValueType {
  FIXED = "FIXED",
  PERCENTAGE = "PERCENTAGE",
  NEW_VALUE = "NEW_VALUE",
}

export enum LoyaltyApplicationLevel {
  CART = "CART",
  ITEM = "ITEM",
}

export type LoyaltyTargetEntity = {
  entityId: string;
  minimumQuantity?: number;
  maximumQuantity?: number;
  maxQuantityPerItem?: number;
};

/** How an offer's value is spread over the items it covers. */
export enum LoyaltyRewardDistribution {
  PER_ENTITY = "PER_ENTITY",
  AGGREGATE = "AGGREGATE",
}

export type LoyaltyTargetConfig = {
  targetType: LoyaltyTargetType;
  minimumItemCount: number;
  maximumItemCount?: number;
  /** AGGREGATE pools the value over the whole selection — see `isBundleShaped`. */
  rewardDistribution?: LoyaltyRewardDistribution;
  targetEntities: LoyaltyTargetEntity[];
};

export enum LoyaltyOfferCategory {
  PERCENTAGE_DISCOUNT = "PERCENTAGE_DISCOUNT",
  FIXED_DISCOUNT = "FIXED_DISCOUNT",
  DELIVERY_DISCOUNT = "DELIVERY_DISCOUNT",
  ITEM_DISCOUNT = "ITEM_DISCOUNT",
  CATEGORY_DISCOUNT = "CATEGORY_DISCOUNT",
  BUNDLE = "BUNDLE",
  BOGO = "BOGO",
  CART_POINTS = "CART_POINTS",
  ITEM_POINTS = "ITEM_POINTS",
  CATEGORY_POINTS = "CATEGORY_POINTS",
}

export enum OfferIconSlug {
  Gift = "gift",
  Percentage = "percentage",
  Money = "money", // dollar
  Star = "star",
  Multiplier = "multiplier",
  Delivery = "delivery",
  Bogo = "bogo",
  Bundle = "bundle",
}

export enum LoyaltyOfferSystem {
  MANUAL = "MANUAL",
  SPLASH = "SPLASH",
  MILESTONE = "MILESTONE",
  PARTNERSHIP = "PARTNERSHIP",
}

type LocalizedStringArray = Partial<Record<I18LanguagesEnum, string[]>>;

interface OfferCardConditionLines {
  full: LocalizedString;
  short: LocalizedString;
}

interface OfferUiTextResolved {
  title: LocalizedString;
  subTitle?: LocalizedString;
  conditions?: LocalizedString[];
  description?: LocalizedString;
  /** Pre-formatted expiry line from the loyalty service (`buildValidUntilLocalized`). */
  validUntil?: LocalizedString;
  imageUrls?: string[];
}

/**
 * Per-group copy for the offer page's selection step. Sent by the loyalty
 * service so count/pluralisation stays in one place across clients; the
 * frontend falls back to generic copy for offers issued before this shipped
 * (see `resolveOfferGroupCopy`).
 */
interface OfferSelectionGroupUi {
  kind: "BUY" | "REWARD";
  title: LocalizedString;
}

export enum OfferBackgroundTextStyle {
  Alternate = "alternate",
}

interface OfferBackgroundTextLine {
  text: string;
  style?: OfferBackgroundTextStyle;
}

/** Per-language segmented background lines for offer cards. */
type LocalizedOfferBackgroundText = Partial<
  Record<I18LanguagesEnum, OfferBackgroundTextLine[]>
>;

interface OfferCardUi {
  titleText: LocalizedStringArray;
  backgroundText?: LocalizedOfferBackgroundText;
  conditions?: OfferCardConditionLines;
}

export enum LoyaltyOfferGraphicEntityType {
  ITEM = "item",
  CATEGORY = "category",
}

export type LoyaltyOfferGraphic =
  | {
      type: "image";
      entityId: string;
      entityType: LoyaltyOfferGraphicEntityType;
    }
  | { type: "icon"; value: OfferIconSlug }
  | { type: "text"; value: string };

export interface OfferIconUi {
  type: "icon" | "image" | "text";
  value: OfferIconSlug;
}

interface CartCardUi {
  label: LocalizedString;
  subText?: LocalizedString;
  showPriceBeforeDiscount: boolean;
}

interface OfferRemovePopup {
  subText: LocalizedString;
  buttonText: LocalizedString;
}

interface OfferPopups {
  removePopup: OfferRemovePopup;
  /** Template for "need more points to unlock"; use `{points}` in copy for client-side interpolation. */
  lockedText?: LocalizedString;
}

export interface OfferUiConfigResolved extends OfferUiTextResolved {
  offerCard: OfferCardUi;
  graphics: LoyaltyOfferGraphic[];
  icon: OfferIconUi;
  cartCard: CartCardUi;
  popup: OfferPopups;
  /** Guest-built offers (BOGO / bundle) only. Absent on older offers. */
  selectionPage?: {
    groups: OfferSelectionGroupUi[];
    /** Longer body for the offer page, which has room to name the items. */
    description?: LocalizedString;
  };
}

type LoyaltyOfferDateTime = {
  /** date in format YYYY-MM-DD */
  date: Date;
  /** time in format HH:MM */
  time?: string;
};

type LoyaltyOfferDateRange = {
  start?: LoyaltyOfferDateTime;
  end?: LoyaltyOfferDateTime;
};

type LoyaltyOfferTimeRange = {
  start?: string;
  end?: string;
};

export type LoyaltyOfferValidity = {
  minimumSpendAmount: number;
  dateRange: LoyaltyOfferDateRange;
  dailyTime?: LoyaltyOfferTimeRange;
  validDaysOfWeek?: LoyaltyDayOfWeek[];
  maxUsesPerCustomer: number;
  requirements?: LoyaltyTargetConfig;
};

export type LoyaltyOfferApplicationScope = {
  applyToBasePrice?: boolean;
  applyToVat?: boolean;
  applyToService?: boolean;
  includeExcludedItems?: boolean;
  applyToDelivery?: boolean;
  includeAddons?: boolean;
};

export type LoyaltyOfferType = {
  id: string;
  shortId?: string;
  label?: string;
  rewardType: LoyaltyRewardType;
  offerCategory: LoyaltyOfferCategory;
  valueType: LoyaltyValueType;
  value: number;
  maxRewardAmount?: number;
  applicationLevel: LoyaltyApplicationLevel;
  uiConfig?: OfferUiConfigResolved;
  applicationScope: LoyaltyOfferApplicationScope;
  isActive: boolean;
  validity: LoyaltyOfferValidity;
  itemLevelConfig?: LoyaltyTargetConfig;
  channels?: LoyaltyChannel[];
  orderTypes?: LoyaltyOrderType[];
  milestoneId?: string;
  tierId?: string;
  milestonePoints?: number;
  offerSystem: LoyaltyOfferSystem;
};

/** Redux `entityImageLookup`: maps entity uuid (and numeric id fallback) → menu image + parent item id. */
export type EntityImageLookup = Record<
  string,
  { itemImage: string; itemId?: number }
>;

export type IsLoyaltyOfferApplicableArgs = {
  orderType?: LoyaltyOrderType;
  channel?: LoyaltyChannel;
  timeZone: string;
  formatTime?: (time: string) => string;
  entityImageLookup?: EntityImageLookup;
  itemStatusRedux?: ItemStatusSliceType;
};

export enum LoyaltyOfferInapplicableReason {
  INACTIVE = "INACTIVE",
  EXPIRED = "EXPIRED",
  DAILY_TIME_OUTSIDE_WINDOW = "DAILY_TIME_OUTSIDE_WINDOW",
  INVALID_DAY_OF_WEEK = "INVALID_DAY_OF_WEEK",
  INVALID_ORDER_TYPE = "INVALID_ORDER_TYPE",
  INVALID_CHANNEL = "INVALID_CHANNEL",
  ITEMS_UNAVAILABLE = "ITEMS_UNAVAILABLE",
}

/** Query params for GET proxy-loyalty/tier-system/customer (customer from JWT). */
export type LoyaltyTierSystemParams = {
  restaurantId: number;
  /** Comma-separated language codes for tier ui-details localization. */
  languages?: string;
  /**
   * Offline-mode guests, same contract as the customer-info call above. Without
   * it the proxy has no customer for a visitor token and loyalty answers in
   * catalog mode, so the guest sees points from /customer/info but a catalog
   * tier list and no progress.
   */
  customerShortId?: string;
};

/** Localized labels from loyalty proxy (locale keys vary). */
type LoyaltyTierSystemLocalizedStrings = Partial<
  Record<"en" | "ar" | "fr" | "ar-SA", string>
> &
  Record<string, string | undefined>;

type LoyaltyTierSystemRulesByLocale = Partial<
  Record<"en" | "ar" | "ar-SA", string[]>
> &
  Record<string, string[] | undefined>;

// type LoyaltyTierSystemOfferValidity = {
//   isActive?: boolean;
//   minimumSpendAmount?: number;
//   dateRange?: Record<string, unknown>;
// };

// type LoyaltyTierSystemOfferApplicationScope = {
//   applyToBasePrice?: boolean;
//   applyToVat?: boolean;
//   applyToService?: boolean;
// };

// type LoyaltyTierSystemOfferConfiguration = {
//   value?: number;
//   valueType?: string;
//   applicationScope?: LoyaltyTierSystemOfferApplicationScope;
// };

// type LoyaltyTierSystemOffer = {
//   id: string;
//   rewardType?: string;
//   validity?: LoyaltyTierSystemOfferValidity;
//   configuration?: LoyaltyTierSystemOfferConfiguration;
// };

export type LoyaltyTierSystemMilestone = {
  milestoneId: string;
  name: LoyaltyTierSystemLocalizedStrings;
  points: number;
  offers: LoyaltyOfferType[];
};

export type LoyaltyTierSystemTier = {
  tierId: string;
  tierName: LoyaltyTierSystemLocalizedStrings;
  tierOrder: number;
  coolDownPeriodDays?: number;
  minUnitsToStay?: number;
  minUnitsToEnter?: number;
  earningRate?: number;
  redemptionRate?: number;
  redemptionIncrement?: number;
  rules?: LoyaltyTierSystemRulesByLocale;
  milestones?: LoyaltyTierSystemMilestone[];
  customCategoryEarningRates?: { categoryId: string; earningRate: number }[];
  customItemEarningRates?: { itemId: string; earningRate: number }[];
  color?: string;
};

/** Full tier-system payload from proxy-loyalty/tier-system/customer. */
export type LoyaltyTierSystem = {
  tierSystemId: number;
  tierSystemUuid: string;
  tiers: LoyaltyTierSystemTier[];
  tierSystemUnitType?: "ORDERS" | "POINTS";
  pointsName?: LoyaltyTierSystemLocalizedStrings;
  /** loyalty-service `PointsIcon`; only STAR has Figma artwork so far. */
  pointsIcon?: "STAR" | "DRUMSTICK" | "BEANS" | "BUN";
  pointsExpireAfterMonths?: number;
  mechanism?: LoyaltyMechanism;
  /**
   * loyalty-service `GUEST_TIERS_UI`: the rollout gate for the new tiers and
   * milestones storefront. Which of its surfaces actually render is read off
   * `mechanism` - see the loyalty-service selectors.
   */
  newLoyaltyUiEnabled?: boolean;
};

type LoyaltyOfferReasonPayload = {
  dailyTimeText?: string;
};

export type LoyaltyOfferReasonDescriptor = {
  key?: string;
  params?: Record<string, string>;
};

export type LoyaltyOfferApplicabilityResult = {
  applicable: boolean;
  reason?: LoyaltyOfferInapplicableReason;
  payload?: LoyaltyOfferReasonPayload;
  descriptor?: LoyaltyOfferReasonDescriptor;
};

export type LoyaltyApplyPromoCodeResponse = {
  valid: boolean;
  offer?: LoyaltyOfferType;
};
